import { ApplicationConfig, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { provideRouter, withComponentInputBinding, withPreloading } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { routes } from './app.routes';
import { errorInterceptor } from '@core/interceptors/error.interceptor';
import { loadingInterceptor } from '@core/interceptors/loading.interceptor';
import { demoRoleInterceptor } from '@core/interceptors/demo-role.interceptor';
import { projectInterceptor } from '@core/interceptors/project.interceptor';
import { GlobalErrorHandler } from '@core/error-handling/global-error-handler';
import { TelemetryService } from '@core/telemetry/telemetry.service';
import { AuthService } from '@core/services/auth.service';
import { WELLS_NPT_ENABLED } from '@core/services/wells-context.service';
import { msalProviders } from '@core/auth/msal.config';
import { IdlePreloadingStrategy } from '@core/config/idle-preloading.strategy';
import { environment } from '@env';

function initTelemetry(telemetry: TelemetryService) {
  return () => telemetry.init();
}

function initAuth(auth: AuthService) {
  return () => auth.initFromApi();
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: WELLS_NPT_ENABLED, useValue: true },
    provideRouter(routes, withComponentInputBinding(), withPreloading(IdlePreloadingStrategy)),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([loadingInterceptor, errorInterceptor, demoRoleInterceptor, projectInterceptor]), withInterceptorsFromDi()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideNativeDateAdapter(),
    // Render all DatePipe timestamps in US Central time (CST/CDT) regardless of the viewer's
    // local machine timezone, so audit-trail hours etc. are consistent for every user.
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: 'America/Chicago' } },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: initTelemetry,
      deps: [TelemetryService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initAuth,
      deps: [AuthService],
      multi: true,
    },
    // Azure AD SSO via MSAL (only when clientId is configured)
    ...(environment.auth.clientId ? msalProviders : []),
  ],
};
